/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ColorProps } from '#utils/UNSAFE_interpolations/colors';
import { BarItemData, BarSeriesData } from '../barChart.types';

export function getBarProps<K, D>(
  item: BarItemData<K>,
  series: BarSeriesData<K, D>,
  seriesColor: ColorProps['color']
) {
  const fill = item.color || series.color || seriesColor;
  const stroke = item.borderColor || series.borderColor;
  return { fill, stroke };
}

/**
 * Returns the coordinate of bar rect.
 * @param x1 The bar start coordinate in x axis.
 * @param x2 The bar end coordinate in x axis.
 * @param endCoord The coordinate of bar top in y axis.
 * @param baselineCoord The coordinate of bar base in y axis.
 * @param isHoriz Whether the chart is horizontal chart.
 * @returns
 */
export function getRectCoord(
  x1: number,
  x2: number,
  endCoord: number,
  baselineCoord: number,
  isHoriz: boolean
) {
  let x: number;
  let y: number;
  let width: number;
  let height: number;

  let x_1 = x1;
  let x_2 = x2;

  // Bar width has to be at least 1px to prevent disappearing bars
  let barWidth = x2 - x1;
  if (barWidth < 1) {
    x_1 = Math.floor(x_1);
    x_2 = x_1 + 1;
    barWidth = 1;
  }

  if (!isHoriz) {
    x = x_1;
    y = endCoord > baselineCoord ? baselineCoord : endCoord;
    width = Math.abs(x_2 - x_1);
    height = Math.abs(baselineCoord - endCoord);
  } else {
    y = x_1;
    x = endCoord > baselineCoord ? baselineCoord : endCoord;
    width = Math.abs(baselineCoord - endCoord);
    height = Math.abs(x_2 - x_1);
  }

  return { x, y, width, height };
}
